import React, {useState} from "react";
import {GenerateQuoteForm} from "./styles"
import {MainLayout} from "../../layouts/MainLayout";
import {useTranslation} from "react-i18next";
import useFetchEmployees from "../../hooks/useFetchEmployees";
import {useNavigate, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {fullNameForUser, isNonNegativeNumber, isPositiveNumber} from "../../utils";
import {currencySymbolByCode} from "../../utils/currencies";
import {FETCH_STATUS_IDLE, FETCH_STATUS_LOADING} from "../../utils/constants";
import {
    InputFieldsWrapper,
    InputSingleFieldWrapper,
    InputText,
    PopupLoading,
    PrimaryButton
} from "../../components";
import {
    allEmployeesFetchState,
    selectEmployeeById,
    updateEmployeeState
} from "../../app/employee/selectors";
import {selectAdminUser} from "../../app/adminUser/selectors";
import {sendQuote} from "../../app/employee/actions";

export interface QuoteFormFields {
    social_taxes: string,
    fee: string,
    setup_fee: string,
    security_deposit: string,
    first_payment_total: string,
    monthly_payment_total: string,
    [key: string]: string; // ImportantMessage definition
}

interface FormErrors {
    social_taxes: boolean,
    fee: boolean,
    setup_fee: boolean,
    security_deposit: boolean
}

const isLastCharEuroSign = (string: string): boolean => {
    const regex = /€$/;
    return regex.test(string);
};

const extractNumericValue = (value: string): number => {
    const numericValue = parseFloat(value.replace(/[€$]/g, '').trim());
    return Number.isNaN(numericValue) ? 0 : numericValue;
};

const GenerateQuote = () => {
    useFetchEmployees()
    const params = useParams()
    const dispatch = useDispatch()
    const id = params.id || ''

    const employee = useSelector(selectEmployeeById(id));
    const updateStatus = useSelector(updateEmployeeState);
    const fetchState = useSelector(allEmployeesFetchState);

    const admin = useSelector(selectAdminUser);

    const [quote, setQuote] = useState<QuoteFormFields>({
        social_taxes: '€',
        fee: '€',
        setup_fee: '€',
        security_deposit: '€',
        monthly_payment_total: '0',
        first_payment_total: '0'
    })
    const [errors, setErrors] = useState<FormErrors>({
        social_taxes: false,
        fee: false,
        setup_fee: false,
        security_deposit: false
    })

    const {t} = useTranslation();
    const navigate = useNavigate();

    if (fetchState === FETCH_STATUS_IDLE || fetchState === FETCH_STATUS_LOADING || updateStatus === FETCH_STATUS_LOADING) {
        return <MainLayout>
            <PopupLoading isOpen={true}/>
        </MainLayout>
    }


    if(!employee){
        return navigate('/employees')
    }

    const handleInputChange = (e: React.SyntheticEvent) => {
        const key = (e.target as HTMLElement).id || (e.target as HTMLInputElement).name;
        const { value } = e.target as HTMLInputElement;

        const isFieldValid = (key === 'fee' || key === 'setup_fee') 
            ? isNonNegativeNumber(value) 
            : isPositiveNumber(value);

        setErrors((prevState) => ({
            ...prevState,
            [key]: !isFieldValid,
        }));

        setQuote((prevState: QuoteFormFields) => {

            //Remove irrelevant fields for calculation
            delete prevState[key];
            delete (prevState as any).monthly_payment_total;
            delete (prevState as any).first_payment_total;

            const isSalaryInEuro = employee.salary.currency.toUpperCase() === 'EUR';

            let totalFirstPayment = isSalaryInEuro ? employee.salary.amount : 0;
            totalFirstPayment += Object.keys(prevState).reduce((acc, curr) => {
                return acc + extractNumericValue(prevState[curr])
            }, Number(value) || 0)

            let totalRemainingMonths = isSalaryInEuro ? employee.salary.amount : 0;
            totalRemainingMonths += Object.keys(prevState).reduce((acc, curr) => {
                if(curr !== 'security_deposit' && curr !== 'setup_fee'){
                    return acc + extractNumericValue(prevState[curr])
                }
                return acc;
            }, key !== 'security_deposit' && key !== 'setup_fee' ? Number(value) || 0 : 0)

            return {
                ...prevState,
                [key]: value,
                first_payment_total: isSalaryInEuro ? `${totalFirstPayment.toFixed(2)}€` :
                    t('total_cost_employee', {
                        total: totalFirstPayment,
                        salary: employee.salary.amount,
                        currency: currencySymbolByCode(employee.salary.currency),
                    }),
                monthly_payment_total: isSalaryInEuro ? `${totalRemainingMonths.toFixed(2)}€` :
                    t('total_cost_employee', {
                        total: totalRemainingMonths,
                        salary: employee.salary.amount,
                        currency: currencySymbolByCode(employee.salary.currency),
                    }),
            };
        });
    };
    const handleInputOnFocus = (e: React.SyntheticEvent) => {
        const key = (e.target as HTMLElement).id || (e.target as HTMLInputElement).name;
        const {value} = e.target as HTMLInputElement;

        if (isLastCharEuroSign(value)) {
            setQuote((prevState) => {
                return {
                    ...prevState,
                    [key]: value.slice(0, -1)
                };
            });
        }
    };
    const handleInputOnBlur = (e: React.SyntheticEvent) => {
        const key = (e.target as HTMLElement).id || (e.target as HTMLInputElement).name;
        const {value} = e.target as HTMLInputElement;
        if (!isLastCharEuroSign(value)) {
            setQuote((prevState) => {
                return {
                    ...prevState,
                    [key]: value + '€'
                };
            });
        }
    }


    const noErrors = (): boolean => Object.values(errors).every((value) => value === false);

    const checkFields = (): boolean => {
        return !isPositiveNumber(extractNumericValue(quote.social_taxes)) ||
            !isNonNegativeNumber(extractNumericValue(quote.fee)) ||
            !isPositiveNumber(extractNumericValue(quote.security_deposit)) ||
            !isNonNegativeNumber(extractNumericValue(quote.setup_fee)) ||
            !noErrors()
    }

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        dispatch(sendQuote({
            employeeId: employee._id,
            companyId: employee.company._id,
            social_taxes: extractNumericValue(quote.social_taxes),
            fee: extractNumericValue(quote.fee),
            security_deposit: extractNumericValue(quote.security_deposit),
            setup_fee: extractNumericValue(quote.setup_fee)
        }) as any)
        navigate('/employee/' + id)
    }

    return <MainLayout>
        <GenerateQuoteForm onSubmit={handleSubmit}>
            <h1>{t('create_quote_title', {employee_name: fullNameForUser(employee)})}</h1>
            <section>
                <h3>{t('contract_details')}</h3>
                <InputFieldsWrapper>
                    <InputText
                        disabled
                        label={t('contract_start_date') as string}
                        value={new Date(employee.start_date).toLocaleDateString(admin.locale, {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                        })}
                    />
                    <InputText
                        disabled
                        label={t('contract_type') as string}
                        value={t('fixed_date') as string}
                    />
                </InputFieldsWrapper>
                <InputFieldsWrapper>
                    <InputText
                        disabled
                        label={t('entity') as string}
                        value={employee.company.name}
                    />
                    <InputText
                        disabled
                        label={t('job_title') as string}
                        value={employee.activity}
                    />
                </InputFieldsWrapper>
            </section>
            <section>
                <h3>{t('payment_details')}</h3>
                <InputFieldsWrapper>
                    <InputText
                        disabled
                        label={t('gross_salary') as string}
                        value={`${employee.salary.amount}${currencySymbolByCode(employee.salary.currency)}`}
                    />
                    <InputText
                        disabled
                        label={t('invoice_cycle_ends') as string}
                        value={t('last_day_of_the_month') as string}
                    />
                </InputFieldsWrapper>
                <InputFieldsWrapper>
                    <InputText
                        disabled
                        label={t('payment_due') as string}
                        value={t('same_day') as string}
                    />
                    <InputText
                        disabled
                        label={t('pay_ahead_of_the_weekend') as string}
                        value={t('on') as string}
                    />
                </InputFieldsWrapper>
                <InputFieldsWrapper>
                    <InputText
                        label={t('social_taxes') as string}
                        value={quote.social_taxes}
                        id='social_taxes'
                        name='social_taxes'
                        onChange={(e) => handleInputChange(e)}
                        onFocus={(e)=> handleInputOnFocus(e)}
                        onBlur={(e)=> handleInputOnBlur(e)}
                        status={errors.social_taxes ? 'danger' : 'normal'}
                        helperText={errors.social_taxes ? t('enter_a_valid_number') as string : ''}
                    />
                    <InputText
                        label={t('eor_fee') as string}
                        value={quote.fee}
                        id='fee'
                        name='fee'
                        onChange={(e) => handleInputChange(e)}
                        onFocus={(e)=> handleInputOnFocus(e)}
                        onBlur={(e)=> handleInputOnBlur(e)}
                        status={errors.fee ? 'danger' : 'normal'}
                        helperText={errors.fee ? t('enter_a_valid_number') as string : ''}
                        tooltipText={t('add_companio_fee') as string}
                    />
                </InputFieldsWrapper>
                <InputSingleFieldWrapper>
                    <InputText
                        disabled
                        label={t('total') as string}
                        value={quote.monthly_payment_total}
                        id='monthly_payment_total'
                        name='monthly_payment_total'
                    />
                </InputSingleFieldWrapper>
            </section>
            <section>
                <h3>{t('first_payment')}</h3>
                <InputFieldsWrapper>
                    <InputText
                        label={t('security_deposit') as string}
                        value={quote.security_deposit}
                        id='security_deposit'
                        name='security_deposit'
                        onChange={(e) => handleInputChange(e)}
                        onFocus={(e)=> handleInputOnFocus(e)}
                        onBlur={(e)=> handleInputOnBlur(e)}
                        status={errors.security_deposit ? 'danger' : 'normal'}
                        helperText={errors.security_deposit ? t('enter_a_valid_number') as string : ''}
                    />
                    <InputText
                        label={t('setup_fee') as string}
                        value={quote.setup_fee}
                        id='setup_fee'
                        name='setup_fee'
                        onChange={(e) => handleInputChange(e)}
                        onFocus={(e)=> handleInputOnFocus(e)}
                        onBlur={(e)=> handleInputOnBlur(e)}
                        status={errors.setup_fee ? 'danger' : 'normal'}
                        helperText={errors.setup_fee ? t('enter_a_valid_number') as string : ''}
                        tooltipText={t('add_companio_fee') as string}
                    />
                </InputFieldsWrapper>
                <InputSingleFieldWrapper>
                    <InputText
                        disabled
                        label={t('total') as string}
                        value={quote.first_payment_total}
                        id='first_payment_total'
                        name='first_payment_total'
                    />
                </InputSingleFieldWrapper>
            </section>
            <div className="send_quote">
                <PrimaryButton type="submit" disabled={checkFields()}>{t('send_quote')}</PrimaryButton>
            </div>
        </GenerateQuoteForm>
    </MainLayout>
}

export default GenerateQuote;
